/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { createContext, useContext, useState, useEffect } from "react";
import deliveryapi from "../../services/deliveryapi";

// Create the context
const DeliveryContext = createContext();

// Custom hook for using the context
export const useDelivery = () => {
  const context = useContext(DeliveryContext);
  if (!context) {
    throw new Error("useDelivery must be used within a DeliveryProvider");
  }
  return context;
};

// Provider component
export const DeliveryProvider = ({ children }) => {
  // State for delivery data
  const [deliveryData, setDeliveryData] = useState(null);
  const [filteredData, setFilteredData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);
  const [completingTasks, setCompletingTasks] = useState(new Set());
  const [tempDeliveryInfo, setTempDeliveryInfo] = useState(null);
  const [formMessage, setFormMessage] = useState(null);
  const [showTcValidationModal, setShowTcValidationModal] = useState(false);
  const [pendingDeliveryMethod, setPendingDeliveryMethod] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [filterCounts, setFilterCounts] = useState({
    total: 0,
    delivered: 0,
    undelivered: 0,
    assigned: 0,
    unassigned: 0,
    assignable: 0,
    unassignable: 0,
  });
  
  // Add filter state
  const [showDelivered, setShowDelivered] = useState(true);
  const [showUndelivered, setShowUndelivered] = useState(true);
  const [showAssigned, setShowAssigned] = useState(true);
  const [showUnassigned, setShowUnassigned] = useState(true);
  const [deliveryPersonFilter, setDeliveryPersonFilter] = useState(null);
  const [selectedLocality, setSelectedLocality] = useState("");
  const [selectedStreet, setSelectedStreet] = useState("");
  const [searchKey, setSearchKey] = useState("");
  const [filteringInProgress, setFilteringInProgress] = useState(false);

  // Add a debounce mechanism for fetchDeliveryData
  const [lastFetchTime, setLastFetchTime] = useState(0);
  const FETCH_COOLDOWN = 2000; // 2 seconds

  // Add a state to track if we're currently filtering by search key
  const [isSearchKeyActive, setIsSearchKeyActive] = useState(false);

  // DELIVERY LIST DISABLED: No backend calls - fetchDeliveryData returns empty data (re-enable by uncommenting the try block below)
  const fetchDeliveryData = async (force = false) => {
    setLoading(false);
    setReady(true);
    const emptyGrouped = {};
    setDeliveryData(emptyGrouped);
    setFilteredData(emptyGrouped);
    setFilterCounts({ assignable: 0, unassignable: 0, total: 0, delivered: 0, undelivered: 0, assigned: 0, unassigned: 0 });
    return emptyGrouped;
    // --- RE-ENABLE: uncomment below and remove the return above ---
    // const now = Date.now();
    // setLoading(true);
    // setReady(false);
    // if (!force && now - lastFetchTime < FETCH_COOLDOWN) {
    //   setLoading(false);
    //   setReady(true);
    //   return deliveryData;
    // }
    // try {
    //   setError(null);
    //   setLastFetchTime(now);
    //   const data = await deliveryapi.getAllTasks();
    //   const groupedData = data.reduce((acc, item) => {
    //     const locality = item.mahalle;
    //     if (!acc[locality]) acc[locality] = [];
    //     acc[locality].push(item);
    //     return acc;
    //   }, {});
    //   setDeliveryData(groupedData);
    //   setFilteredData(groupedData);
    //   const allTasks = Object.values(groupedData).flat();
    //   setFilterCounts({
    //     assignable: allTasks.filter((t) => !t.lastUpdate?.delivered && !t.lastUpdate?.assignedToUserId).length,
    //     unassignable: allTasks.filter((t) => !t.lastUpdate?.delivered && t.lastUpdate?.assignedToUserId).length,
    //   });
    //   setLoading(false);
    //   setTimeout(() => setReady(true), 300);
    //   return groupedData;
    // } catch (err) {
    //   console.error("Error fetching delivery data:", err);
    //   setError("Veri yüklenirken bir hata oluştu.");
    //   setLoading(false);
    //   setReady(true);
    //   return null;
    // }
  };

  // Update a single task
  const updateTask = async (taskData) => {
    try {
      const response = await deliveryapi.update(taskData);

      // Update local state
      if (deliveryData) {
        const updateTaskInData = (prevData) => {
          if (!prevData) return prevData;

          const newData = { ...prevData };
          Object.keys(newData).forEach((locality) => {
            newData[locality] = newData[locality].map((person) =>
              person.key === taskData.key
                ? {
                    ...person,
                    delivered: response.delivered,
                    deliveryMethod: taskData.deliveryMethod,
                    lastUpdate: response,
                  }
                : person
            );
          });
          return newData;
        };

        setDeliveryData((prevData) => updateTaskInData(prevData));
        setFilteredData((prevData) => updateTaskInData(prevData));
      }

      return response;
    } catch (err) {
      console.error("Error updating task:", err);
      throw err;
    }
  };

  // Validate TC for a task
  const validateTC = async (data) => {
    try {
      const response = await deliveryapi.validateTC(data);

      if (
        response.statusCode === "NOT_FOUND" ||
        response.statusCodeValue === 404
      ) {
        if (response.body?.message?.includes("No records found with the key")) {
          throw new Error(
            "Girilen TC Kimlik Numarası bu görev için geçerli değil. Lütfen kontrol ediniz."
          );
        } else {
          throw new Error(
            "TC Kimlik No doğrulanamadı. Lütfen tekrar deneyiniz."
          );
        }
      }

      return response;
    } catch (error) {
      console.error("TC validation error:", error);

      if (!error.message.includes("TC Kimlik")) {
        throw new Error(
          "TC Kimlik No doğrulama işlemi sırasında bir hata oluştu. Lütfen tekrar deneyiniz."
        );
      }

      throw error;
    }
  };

  // Find a task by key
  const findTaskByKey = (key) => {
    if (!deliveryData) return null;

    return Object.values(deliveryData)
      .flat()
      .find((task) => task.key === key);
  };

  // Assign tasks to a delivery person
  const assignTasks = async (tasks, deliveryPersonId) => {
    try {
      // First update the local state immediately for instant UI feedback
      const updateAssignments = (prevData) => {
        if (!prevData) return prevData;
        const newData = { ...prevData };
        Object.keys(newData).forEach((locality) => {
          newData[locality] = newData[locality].map((person) => {
            if (tasks.includes(person.key)) {
              return {
                ...person,
                lastUpdate: {
                  ...person.lastUpdate,
                  assignedToUserId: deliveryPersonId,
                  assignedByUserId: currentUserId,
                },
              };
            }
            return person;
          });
        });
        return newData;
      };

      // Update both data sources immediately
      setDeliveryData((prevData) => updateAssignments(prevData));
      
      // Update filtered data with a special flag to prevent filter reapplication
      const updatedFilteredData = updateAssignments(filteredData);
      setFilteredData(updatedFilteredData);
      
      // Note: Filter counts are now updated in the TaskActions component
      // to ensure consistent UI updates

      // Then make the API call
      await deliveryapi.assignTasks(tasks, deliveryPersonId);

      return true;
    } catch (err) {
      console.error("Error assigning tasks:", err);
      throw err;
    }
  };

  // Unassign tasks
  const unassignTasks = async (taskIds) => {
    try {
      // First update the local state immediately for instant UI feedback
      const updateUnassignments = (prevData) => {
        if (!prevData) return prevData;
        const newData = { ...prevData };
        Object.keys(newData).forEach((locality) => {
          newData[locality] = newData[locality].map((person) => {
            if (taskIds.includes(person.key)) {
              return {
                ...person,
                lastUpdate: {
                  ...person.lastUpdate,
                  assignedToUserId: null,
                  assignedByUserId: null,
                },
              };
            }
            return person;
          });
        });
        return newData;
      };

      // Update both data sources immediately
      setDeliveryData((prevData) => updateUnassignments(prevData));
      
      // Update filtered data with a special flag to prevent filter reapplication
      const updatedFilteredData = updateUnassignments(filteredData);
      setFilteredData(updatedFilteredData);
      
      // Note: Filter counts are now updated in the TaskActions component
      // to ensure consistent UI updates

      // Then make the API call
      await deliveryapi.unassignTasks(taskIds);

      return true;
    } catch (err) {
      console.error("Error unassigning tasks:", err);
      throw err;
    }
  };

  // Apply filters to the data
  const applyFilters = (filters) => {
    const {
      locality,
      street,
      showDelivered,
      showUndelivered,
      showAssigned,
      showUnassigned,
      deliveryPersonFilter,
      searchKey,
    } = filters;

    if (searchKey) {
      setIsSearchKeyActive(true);
    } else if (filters.hasOwnProperty("searchKey") && !searchKey) {
      setIsSearchKeyActive(false);
    }

    if (!deliveryData) return;

    // Get all tasks from the main data
    let allTasks = Object.values(deliveryData).flat();

    // Apply search filter
    if (searchKey) {
      allTasks = allTasks.filter(
        (task) => task.key.toString() === searchKey.toString()
      );
    }

    // Apply locality filter
    if (locality) {
      allTasks = allTasks.filter((task) => task.mahalle === locality);
    }

    // Apply street filter
    if (street) {
      allTasks = allTasks.filter((task) => task.caddesokak === street);
    }

    // Apply delivery status filters
    allTasks = allTasks.filter((task) => {
      const isDelivered = task.lastUpdate?.delivered;
      if (isDelivered && !showDelivered) return false;
      if (!isDelivered && !showUndelivered) return false;
      return true;
    });

    // Apply assignment status filters
    allTasks = allTasks.filter((task) => {
      const isAssigned = task.lastUpdate?.assignedToUserId;
      if (isAssigned && !showAssigned) return false;
      if (!isAssigned && !showUnassigned) return false;
      return true;
    });

    // Apply delivery person filter
    if (deliveryPersonFilter) {
      allTasks = allTasks.filter(
        (task) => task.lastUpdate?.assignedToUserId === deliveryPersonFilter
      );
    }

    // Calculate filter counts from the filtered tasks
    const assignableTasks = allTasks.filter(
      (task) =>
        !task.lastUpdate?.delivered && !task.lastUpdate?.assignedToUserId
    ).length;

    const unassignableTasks = allTasks.filter(
      (task) => !task.lastUpdate?.delivered && task.lastUpdate?.assignedToUserId
    ).length;

    // Update filter counts
    setFilterCounts({
      assignable: assignableTasks,
      unassignable: unassignableTasks,
    });

    // Group tasks by locality for the filtered data
    const result = allTasks.reduce((acc, task) => {
      const locality = task.mahalle;
      if (!acc[locality]) {
        acc[locality] = [];
      }
      acc[locality].push(task);
      return acc;
    }, {});

    // Update filtered data
    setFilteredData(result);
  };

  // Update the handleDeliveryButtonClick function
  const handleDeliveryButtonClick = (task, method) => {
    const roles = JSON.parse(sessionStorage.getItem("roles") || "[]");
    const isRegularUser = roles.length === 1 && roles[0].name === "ROLE_USER";
    const currentUser = sessionStorage.getItem("username");

    if (!task.lastUpdate?.assignedToUserId) {
      setFormMessage({
        type: "warning",
        text: "Lütfen önce görevi bir teslimat görevlisine atayın.",
      });
      return;
    }

    if (isRegularUser && task.lastUpdate?.assignedToUserId !== currentUser) {
      setFormMessage({
        type: "warning",
        text: "Bu görev size atanmamış.",
      });
      return;
    }

    setTempDeliveryInfo({
      key: task.key,
      deliveryMethod: method,
      assignedToUserId: task.lastUpdate?.assignedToUserId,
    });

    setPendingDeliveryMethod(method);

    if (method === "EMANETEN") {
      setTimeout(() => {
        setShowTcValidationModal(false);
        setFormMessage({
          type: "emaneten",
          text: "Emaneten teslim için onay bekleniyor",
        });
      }, 0);
    } else {
      setTimeout(() => {
        setShowTcValidationModal(true);
      }, 0);
    }
  };

  // Update the showTcValidationModal state setter to include the assignment check
  const safeSetShowTcValidationModal = (value, taskKey) => {
    if (value === true && taskKey) {
      const task = findTaskByKey(taskKey);
      if (task && !task.lastUpdate?.assignedToUserId) {
        setFormMessage({
          type: "warning",
          text: "Lütfen önce görevi bir teslimat görevlisine atayın.",
        });
        return;
      }
    }

    setShowTcValidationModal(value);
  };

  // DELIVERY LIST DISABLED: No initial fetch - no getAllTasks or other backend calls on mount (re-enable by uncommenting below)
  useEffect(() => {
    setLoading(false);
    setReady(true);
    setDeliveryData({});
    setFilteredData({});
  }, []);
  // useEffect(() => {
  //   const fetchAndFilterData = async () => {
  //     const data = await fetchDeliveryData();
  //     const roles = JSON.parse(sessionStorage.getItem("roles") || "[]");
  //     const isRegularUser = roles.length === 1 && roles[0].name === "ROLE_USER";
  //     const currentUser = sessionStorage.getItem("username");
  //     if (isRegularUser && data) {
  //       const filteredForUser = {};
  //       Object.entries(data).forEach(([locality, tasks]) => {
  //         const userTasks = tasks.filter(
  //           (task) => task.lastUpdate?.assignedToUserId === currentUser
  //         );
  //         if (userTasks.length > 0) filteredForUser[locality] = userTasks;
  //       });
  //       setDeliveryData(filteredForUser);
  //       setFilteredData(filteredForUser);
  //     }
  //   };
  //   fetchAndFilterData();
  // }, []);

  // Add filter toggle handlers
  const handleToggleDelivered = (value) => {
    // Show loading indicator immediately
    setFilteringInProgress(true);
    
    // Schedule the filtering operation for the next event loop
    setTimeout(() => {
      applyFilters({
        locality: selectedLocality,
        street: selectedStreet,
        showDelivered: value,
        showUndelivered,
        showAssigned,
        showUnassigned,
        deliveryPersonFilter,
        searchKey,
      });
      // Hide loading indicator when filtering is complete
      setFilteringInProgress(false);
    }, 10);
  };

  const handleToggleUndelivered = (value) => {
    // Show loading indicator immediately
    setFilteringInProgress(true);
    
    // Schedule the filtering operation for the next event loop
    setTimeout(() => {
      applyFilters({
        locality: selectedLocality,
        street: selectedStreet,
        showDelivered,
        showUndelivered: value,
        showAssigned,
        showUnassigned,
        deliveryPersonFilter,
        searchKey,
      });
      // Hide loading indicator when filtering is complete
      setFilteringInProgress(false);
    }, 10);
  };

  const handleToggleAssigned = (value) => {
    // Show loading indicator immediately
    setFilteringInProgress(true);
    
    // Schedule the filtering operation for the next event loop
    setTimeout(() => {
      applyFilters({
        locality: selectedLocality,
        street: selectedStreet,
        showDelivered,
        showUndelivered,
        showAssigned: value,
        showUnassigned,
        deliveryPersonFilter,
        searchKey,
      });
      // Hide loading indicator when filtering is complete
      setFilteringInProgress(false);
    }, 10);
  };

  const handleToggleUnassigned = (value) => {
    // Show loading indicator immediately
    setFilteringInProgress(true);
    
    // Schedule the filtering operation for the next event loop
    setTimeout(() => {
      applyFilters({
        locality: selectedLocality,
        street: selectedStreet,
        showDelivered,
        showUndelivered,
        showAssigned,
        showUnassigned: value,
        deliveryPersonFilter,
        searchKey,
      });
      // Hide loading indicator when filtering is complete
      setFilteringInProgress(false);
    }, 10);
  };

  const handleDeliveryPersonFilterChange = (value) => {
    // Update the state immediately for UI feedback
    setDeliveryPersonFilter(value);
    // Show loading indicator
    setFilteringInProgress(true);
    
    // Schedule the filtering operation for the next event loop
    setTimeout(() => {
      applyFilters({
        locality: selectedLocality, 
        street: selectedStreet,
        showDelivered,
        showUndelivered,
        showAssigned,
        showUnassigned,
        deliveryPersonFilter: value,
        searchKey,
      });
      // Hide loading indicator when filtering is complete
      setFilteringInProgress(false);
    }, 0);
  };
  
  const handleSelectedStreetChange = (value) => {
    setSelectedStreet(value);
    setFilteringInProgress(true);
    
    setTimeout(() => {
      applyFilters({
        locality: selectedLocality,
        street: value,
        showDelivered,
        showUndelivered,
        showAssigned,
        showUnassigned,
        deliveryPersonFilter,
        searchKey,
      });
      setFilteringInProgress(false);
    }, 0);
  };
  
  const handleSelectedLocalityChange = (value) => {
    setSelectedLocality(value);
    setFilteringInProgress(true);
    
    setTimeout(() => {
      applyFilters({
        locality: value,
        street: selectedStreet,
        showDelivered,
        showUndelivered,
        showAssigned,
        showUnassigned,
        deliveryPersonFilter,
        searchKey,
      });
      setFilteringInProgress(false);
    }, 0);
  };
  
  const handleSearchKeyChange = (value) => {
    setSearchKey(value);
    setFilteringInProgress(true);
    
    setTimeout(() => {
      applyFilters({
        locality: selectedLocality,
        street: selectedStreet,
        showDelivered,
        showUndelivered,
        showAssigned,
        showUnassigned,
        deliveryPersonFilter,
        searchKey: value,
      });
      setFilteringInProgress(false);
    }, 0);
  };

  const value = {
    // Data
    deliveryData,
    filteredData,
    loading,
    ready,
    error,
    completingTasks,
    tempDeliveryInfo,
    formMessage,
    currentUserId,
    showTcValidationModal,
    pendingDeliveryMethod,
    filterCounts,

    // Setters
    setDeliveryData,
    setFilteredData,
    setLoading,
    setReady,
    setCompletingTasks,
    setTempDeliveryInfo,
    setFormMessage,
    setShowTcValidationModal: safeSetShowTcValidationModal,
    setPendingDeliveryMethod,
    setFilterCounts,
    setShowDelivered,
    setShowUndelivered,
    setShowAssigned,
    setShowUnassigned,

    // Actions
    fetchDeliveryData,
    updateTask,
    validateTC,
    findTaskByKey,
    assignTasks,
    unassignTasks,
    applyFilters,
    handleDeliveryButtonClick,

    // Filter states
    showDelivered,
    showUndelivered,
    showAssigned,
    showUnassigned,
    deliveryPersonFilter,
    selectedLocality,
    selectedStreet,
    searchKey,
    filteringInProgress,

    // Filter handlers
    handleToggleDelivered,
    handleToggleUndelivered,
    handleToggleAssigned,
    handleToggleUnassigned,
    handleDeliveryPersonFilterChange,
    handleSelectedStreetChange,
    handleSelectedLocalityChange,
    handleSearchKeyChange,
  };

  return (
    <DeliveryContext.Provider value={value}>{children}</DeliveryContext.Provider>
  );
};
